<template>
    <div>
        <rkm-modal
            extra-large
            ref="RkmModal"
            title="Intervenções e/ou procedimentos clínicos realizados"
        >
            <rkm-form
                ref="RkmForm"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="5">
                        <rkm-form-input-autocomplete
                            :repository="repositories.procedimento"
                            :request-query-string-params="requestQueryStringParams.procedimento"
                            :search-fields="fields.procedimento"
                            :text-field="(item) => `${formatProcedimento(item.codigo)} - ${item.nome}`"
                            :validations="validations.procedimento.procedimento"
                            @select="model.procedimento.cid = null"
                            label="Procedimento"
                            v-model="model.procedimento.procedimento"
                        />
                    </rkm-col>

                    <rkm-col cols="5">
                        <rkm-form-input-autocomplete
                            :disabled="!model.procedimento.procedimento"
                            :repository="repositories.cid"
                            :request-query-string-params="requestQueryStringParams.cid"
                            :search-fields="fields.cid"
                            :text-field="(item) => `${formatCid(item.codigo)} - ${item.nome}`"
                            :validations="validations.procedimento.cid"
                            label="CID"
                            v-model="model.procedimento.cid"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :validations="validations.procedimento.quantidade"
                            label="Quantidade"
                            number
                            v-model="model.procedimento.quantidade"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <rkm-form
                ref="RkmFormMateriais"
            >

                <rkm-row>
                    <rkm-col cols="5">
                        <rkm-form-input-autocomplete
                            :repository="repositories.material"
                            :request-query-string-params="requestQueryStringParams.material"
                            :search-fields="fields.material"
                            :text-field="(item) => item.descricaoCompleta"
                            label="Material"
                            v-model="model.material.material"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="model.material.estoque"
                            disabled
                            label="Estoque"
                        >
                            <rkm-input-group-append slot="append">
                                <rkm-button
                                    :disabled="!model.material.material"
                                    @click="loadEstoque"
                                    color="primary"
                                    title="Atualizar estoque"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="sync-alt" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input>
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :validations="validations.material.quantidade"
                            label="Quantidade"
                            type="number"
                            v-model="model.material.quantidade"
                        >
                            <rkm-input-group-append
                                slot="append"
                            >
                                <rkm-button
                                    :disabled="!quantidadeValida"
                                    @click="$refs.ModalLote.open(model.material.entradaItens)"
                                    title="Visualizar lotes"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="box-open" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input>
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :disabled="!quantidadeValida"
                            :label="false"
                            @click="$refs.RkmFormMateriais.validate(editIndex !== null ? update : add)"
                            color="primary"
                        >
                             {{ editIndex !== null ? 'Editar' : 'Incluir' }}
                        </rkm-button>
                    </rkm-col>

                </rkm-row>

                <rkm-row v-if="isProcedimentoCurativo">
                    <rkm-col cols="12">
                        <rkm-form-textarea
                            :maxlength="validations.localCurativo.max"
                            :validations="validations.localCurativo"
                            label="Local do curativo"
                            rows="4"
                            v-model="model.localCurativo"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-area title="Materiais">

                        <rkm-table
                            :fields="fields.materiais"
                            :items="model.materiais"
                            hide-filters
                            hide-pagination
                        >

                            <template v-slot:cell(actions)="{item, index}">

                                <rkm-button
                                    @click="edit(item, index)"
                                    color="primary"
                                    small
                                    title="Editar"
                                >
                                    <rkm-icon icon="pen" />
                                </rkm-button>

                                <rkm-button
                                    @click="destroy(index)"
                                    color="danger"
                                    small
                                    title="Excluir"
                                >
                                    <rkm-icon icon="trash" />
                                </rkm-button>
                            </template>
                        </rkm-table>
                </rkm-area>

            </rkm-form>

            <template v-slot:footer="{cancel}">
                <rkm-button @click="cancel">
                    Fechar
                </rkm-button>

                <rkm-button
                    @click="include"
                    color="primary"
                >
                    Salvar
                </rkm-button>
            </template>
        </rkm-modal>

        <ModalLote
            :model="model.material"
            ref="ModalLote"
        />
    </div>
</template>

<script>
// Components.
import ModalLote from '@/components/almoxarifado/components/lote/Modal'

// Enums.
import procedimento from '@/enums/sigtap/procedimento'

// Mixins.
import {error, formats, object, repository} from '@/mixins'

//Vuex
import {mapActions} from 'vuex'

export default {
    components: {ModalLote},
    computed: {
        requestQueryStringParams() {
            return {
                procedimento: {
                    cbo_lotacao_atual: true,
                    paciente_id: this.data.form.paciente.id,
                    where_not: {
                        codigo: [
                            this.enums.procedimento.AVALIACAO_ANTROPOMETRICA,
                            this.enums.procedimento.GLICEMIA_CAPILAR,
                            this.enums.procedimento.MEDICAO_DE_ALTURA,
                            this.enums.procedimento.MEDICAO_DE_PESO,
                            this.enums.procedimento.AFERICAO_DE_TEMPERATURA,
                            this.enums.procedimento.AFERICAO_PRESSAO_ARTERIAL,
                        ],
                    },
                },
                material: {
                    almoxarifado_check_controle_estoque: true,
                    load_entradas: false,
                },
                cid: this.model.procedimento.procedimento && this.model.procedimento.procedimento.hasCidPrincipal
                    ? {
                        principal: true,
                        procedimento_id: this.model.procedimento.procedimento.id,
                    }
                    : {}
            }
        },
        validations() {
            return {
                procedimento: {
                    procedimento: {required: true},
                    cid: {required: this.model.procedimento.procedimento && !!this.model.procedimento.procedimento.hasCidPrincipal},
                    quantidade: {
                        required: true,
                        min_value: 1,
                        max: 3,
                    },
                },
                localCurativo: {max: 500},
                material: {
                    quantidade: {
                        required: !!this.model.material.material,
                        min_value: 1,
                        max_value: this.model.material.estoque,
                    },
                },
            }
        },
        isProcedimentoCurativo() {
            if (!this.model.procedimento.procedimento) {
                return false
            }

            return [
                this.enums.procedimento.CURATIVO_DE_DEMORA_C_OU_S_PREPARO_BIOMECANICO,
                this.enums.procedimento.CURATIVO_EM_GRANDE_QUEIMADO,
                this.enums.procedimento.CURATIVO_EM_MEDIO_QUEIMADO,
                this.enums.procedimento.CURATIVO_EM_PEQUENO_QUEIMADO,
                this.enums.procedimento.CURATIVO_ESPECIAL,
                this.enums.procedimento.CURATIVO_GRAU_II_C_OU_S_DEBRIDAMENTO,
                this.enums.procedimento.CURATIVO_SIMPLES,
            ].includes(this.model.procedimento.procedimento.codigo)
        },
        quantidadeValida() {
            const quantidade = parseInt(this.model.material.quantidade)

            return quantidade > 0 && quantidade <= this.model.material.estoque
        },
    },
    created() {
        this.repositories = {
            material: this.getConectaSusRepository('cadastro.material'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }
    },
    data: () => ({
        enums: {procedimento},
        editIndex: null,
        fields: {
            material: [
                {key: "codigo_material", searchable: true},
                {key: "descricao", searchable: true},
            ],
            procedimento: [
                {key: 'codigo', searchable: true},
                {key: 'nome', searchable: true},
            ],
            materiais: [
                {key: "descricaoCompleta", label: 'Material', searchable: true},
                {key: "quantidade", label: 'Quantidade', searchable: true},
                {class: 'col-min text-center', key: 'actions', label: 'Ações'},
            ],
            cid: [
                {key: 'codigo', searchable: true},
                {key: 'nome', searchable: true},
            ],
        },
        form: {
            possuiEntradaComLote: false,
        },
        model: {
            localCurativo: null,
            material: {
                entradaItens: [],
                escolheuEntradaItemManualmente: null,
                estoque: null,
                material: null,
                quantidade: null,
            },
            materiais: [],
            procedimento: {
                procedimento: null,
                cid: null,
                quantidade: 1,
            },
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open() {
            this.resetForm()
            this.$refs.RkmModal.show()
        },
        include() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    if (this.data.model.procedimentos.find(procedimento => procedimento.id === this.model.procedimento.id)) {
                        this.showValidationNotification('O procedimento já foi incluído.')
                        return
                    }

                    this.data.model.procedimentos.push({
                        id: this.model.procedimento.procedimento.id,
                        codigo: this.model.procedimento.procedimento.codigo,
                        nome: this.model.procedimento.procedimento.nome,
                        hasInstrumentoRegistroRaas: !!this.model.procedimento.procedimento.hasInstrumentoRegistroRaas,
                        automatico: false,
                        quantidade: this.model.procedimento.quantidade,
                        localCurativo: this.model.localCurativo,
                        materiais: this.model.materiais,
                        cidId: this.model.procedimento.cid ? this.model.procedimento.cid.id : null,
                        cidDescricao: this.model.procedimento.cid ? `${this.formatCid(this.model.procedimento.cid.codigo)} - ${this.model.procedimento.cid.nome}` : null,
                    })

                    this.$refs.RkmModal.hide()
                })
        },
        resetForm() {
            this.form.possuiEntradaComLote = false
            this.model.localCUrativo = null
            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = null
            this.model.material.estoque = null
            this.model.material.material = null
            this.model.material.quantidade = null
            this.model.procedimento.procedimento = null
            this.model.procedimento.quantidade = 1
            this.model.procedimento.cid = null
            this.model.materiais = []
        },
        loadEstoque() {
            this.form.possuiEntradaComLote = false
            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = false
            this.model.material.estoque = 0
            this.model.material.quantidade = null

            this.setLoading()

            this.repositories.material.getEstoque(this.model.material.material.id)
                .then(({data}) => {
                    this.form.possuiEntradaComLote = data.possuiEntradaComLote
                    this.model.material.estoque = data.total
                    this.model.material.entradaItens = data.porEntrada.map(estoqueEntrada => {
                        estoqueEntrada.quantidadeSaida = 0
                        return estoqueEntrada
                    })
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        selectEntradaItens() {
            this.model.material.escolheuEntradaItemManualmente = false
            this.model.material.entradaItens.map(e => e.quantidadeSaida = 0)

            let quantidade = parseInt(this.model.material.quantidade)
            let i = 0

            while (quantidade > 0) {
                const entradaItem = this.model.material.entradaItens[i]
                const estoque = parseInt(entradaItem.estoque)

                if (quantidade > estoque) {
                    entradaItem.quantidadeSaida = estoque
                    quantidade -= estoque
                } else {
                    entradaItem.quantidadeSaida = quantidade
                    quantidade = 0
                }

                i++
            }
        },
        add() {
            this.model.materiais.push({
                id: this.model.material.material.id,
                descricao: this.model.material.material.descricao,
                descricaoCompleta: this.model.material.material.descricaoCompleta,
                quantidade: this.model.material.quantidade,
                materialEscolheuEntradaItemManualmente: this.model.material.escolheuEntradaItemManualmente,
                materialEntradaItens: this.model.material.entradaItens,
            })

            this.clearProcedimento()
        },
        edit(material, index) {
            this.editIndex = index

            this.model.material.material = {
                id: material.id,
                descricaoCompleta: material.descricaoCompleta,
            }

            this.model.material.quantidade = material.quantidade
        },
        update() {

            if (this.editIndex === null) return

            this.$set(this.model.materiais, this.editIndex, {
                id: this.model.material.material.id,
                descricaoCompleta: this.model.material.material.descricaoCompleta,
                quantidade: this.model.material.quantidade,
            })

            this.editIndex = null

            this.clearProcedimento()
        },
        clearProcedimento() {

            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = null
            this.model.material.estoque = null
            this.model.material.material = null
            this.model.material.quantidade = null

            this.$nextTick().then(() => this.$refs.RkmFormMateriais.resetValidator())
        },
        destroy(index) {
            this.showDeleteConfirmation('Deseja realmente excluir o material?', async () => {
                this.model.materiais.splice(index, 1)
            })
        },
    },
    mixins: [error, formats, object, repository],
    props: {
        data: {type: Object, required: true},
    },
    watch: {
        isProcedimentoCurativo() {
            this.model.localCurativo = null
        },
        'model.material.material'(value) {
            this.form.possuiEntradaComLote = false
            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = false
            this.model.material.estoque = 0
            this.model.material.quantidade = null

            if (value && value.id) {
                this.loadEstoque()
            }
        },
        'model.material.quantidade': function () {
            if (this.form.possuiEntradaComLote && this.quantidadeValida) {
                this.selectEntradaItens()
            }
        },
    },
}
</script>